import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { withMainLayout } from "../view/LayoutHOCs";
import * as layoutService from "../view/layoutService";
import { getProcessingTasks } from "../rules/ruleService";
import {
    formatTaskDate,
    getTaskStartTime,
    getTaskStatus,
    getTaskTitle,
    getTaskType,
    hasTaskStartTime,
    isCompleteTask,
    isProcessingTask,
    mergeRefreshedTasks
} from "./processingTasksUtils";
import { InfoBoxes } from "../info-boxes/InfoBoxes";
import { Pagination } from "../view/Pagination";
import ProcessingTaskRowSkeleton from "./ProcessingTaskRowSkeleton";
import {
    cacheProcessingTasks,
    getCachedProcessingTasks,
    getLocalProcessingTasks,
    PROCESSING_TASKS_CHANGED_EVENT
} from "./processingTasksRegistry";

const breadcrumbs = [
    {
        text: "Home",
        redirectTo: "/",
        iconClassName: "icon-home"
    },
    {
        text: "Processing Tasks",
        redirectTo: "/processing-tasks"
    }
];
const PAGE_SIZE = 10;

function updateTaskRow(tasks, updatedTask) {
    if (!updatedTask?.id) {
        return tasks;
    }

    const existingIndex = tasks.findIndex(task => task.id === updatedTask.id);
    if (existingIndex === -1) {
        return [updatedTask, ...tasks];
    }

    return tasks.map(task => task.id === updatedTask.id ? {
        ...task,
        ...updatedTask
    } : task);
}

function ProcessingTasksPage() {
    const history = useHistory();
    const location = useLocation();
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState("processing");
    const [currentPage, setCurrentPage] = useState(0);

    const loadTasks = async () => {
        try {
            const response = await getProcessingTasks();
            const serverTasks = response?.page || [];
            cacheProcessingTasks(serverTasks);
            const refreshedTasks = [...serverTasks, ...getCachedProcessingTasks(), ...getLocalProcessingTasks()];
            setTasks(currentTasks => {
                const mergedTasks = mergeRefreshedTasks(currentTasks, refreshedTasks);
                cacheProcessingTasks(mergedTasks);
                return mergedTasks;
            });
        } catch (error) {
            const refreshedTasks = [...getCachedProcessingTasks(), ...getLocalProcessingTasks()];
            setTasks(currentTasks => {
                const mergedTasks = mergeRefreshedTasks(currentTasks, refreshedTasks);
                cacheProcessingTasks(mergedTasks);
                return mergedTasks;
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        layoutService.setPageTitle("Processing Tasks");
        layoutService.setBreadcrumbs(breadcrumbs);
    }, []);

    useEffect(() => {
        loadTasks();
        const intervalId = setInterval(loadTasks, 5000);
        const onProcessingTaskChanged = event => {
            const updatedTask = event?.detail?.task;
            if (updatedTask) {
                setTasks(currentTasks => updateTaskRow(currentTasks, updatedTask));
                return;
            }

            loadTasks();
        };

        window.addEventListener(PROCESSING_TASKS_CHANGED_EVENT, onProcessingTaskChanged);
        window.addEventListener("storage", loadTasks);
        return () => {
            clearInterval(intervalId);
            window.removeEventListener(PROCESSING_TASKS_CHANGED_EVENT, onProcessingTaskChanged);
            window.removeEventListener("storage", loadTasks);
        };
    }, []);

    const openTask = (task) => {
        history.push(`/processing-tasks?task=${task.id}`);
    };

    const sortedTasks = tasks.filter(hasTaskStartTime).sort((left, right) => {
        const getSortTime = task => {
            const value = getTaskStartTime(task);
            const numericValue = Number(value);
            const dateValue = Number.isNaN(numericValue) ? new Date(value).getTime() : numericValue;
            return Number.isNaN(dateValue) ? 0 : dateValue;
        };
        const timeDifference = getSortTime(right) - getSortTime(left);

        if (timeDifference !== 0) {
            return timeDifference;
        }

        return Number(right.id || 0) - Number(left.id || 0);
    });
    const filteredTasks = sortedTasks.filter(task => {
        if (statusFilter === "processing") {
            return isProcessingTask(task);
        }

        if (statusFilter === "complete") {
            return isCompleteTask(task);
        }

        return true;
    });
    const totalPages = Math.ceil(filteredTasks.length / PAGE_SIZE);
    const paginatedTasks = filteredTasks.slice(
        currentPage * PAGE_SIZE,
        (currentPage + 1) * PAGE_SIZE
    );

    useEffect(() => {
        setCurrentPage(0);
    }, [statusFilter]);

    useEffect(() => {
        if (totalPages > 0 && currentPage >= totalPages) {
            setCurrentPage(totalPages - 1);
        }
    }, [currentPage, totalPages]);

    return (
        <>
            <InfoBoxes />
            <div className="processing-tasks-page container-fluid" key={location.search}>
                <div className="row">
                    <div className="col-12">
                        <div className="card oct_analysis-card-v2">
                            <div className="oct_analysis-header-v2">
                                <div className="oct_analysis-title-section">
                                    <h2>PROCESSING TASKS</h2>
                                </div>
                                <div className="processing-tasks-filters" aria-label="Filter processing tasks">
                                    {["processing", "complete", "all"].map(filter => (
                                        <button
                                            type="button"
                                            key={filter}
                                            className={statusFilter === filter ? "is-active" : ""}
                                            onClick={() => setStatusFilter(filter)}
                                        >
                                            {filter.charAt(0).toUpperCase() + filter.slice(1)}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="card-body">
                                <div className="oct_table-responsive-wrapper-v2">
                                    <table className="oct_table-v2 processing-tasks-table">
                                        <thead>
                                            <tr>
                                                <th>Task</th>
                                                <th>Device/Target</th>
                                                <th>Type</th>
                                                <th>Started</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loading ? (
                                                Array.from({ length: PAGE_SIZE }, (_, index) => (
                                                    <ProcessingTaskRowSkeleton key={index} index={index} />
                                                ))
                                            ) : paginatedTasks.map(task => (
                                                <tr
                                                    key={task.id}
                                                    onClick={() => openTask(task)}
                                                >
                                                    <td><strong>{getTaskTitle(task)}</strong><small>{task.details || "-"}</small></td>
                                                    <td>{task.device || task.target || "-"}</td>
                                                    <td>{getTaskType(task)}</td>
                                                    <td>{formatTaskDate(getTaskStartTime(task))}</td>
                                                    <td>
                                                        {getTaskStatus(task) && (
                                                            <span className={`processing-task-status is-${getTaskStatus(task).toLowerCase()}`}>
                                                                {getTaskStatus(task)}
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {!loading && filteredTasks.length === 0 && (
                                    <div className="oct_empty-state-v2">
                                        <div className="oct_empty-state-title">No {statusFilter === "all" ? "" : `${statusFilter} `}tasks</div>
                                        <div>No tasks match the selected status.</div>
                                    </div>
                                )}

                                {!loading && filteredTasks.length > 0 && (
                                    <Pagination
                                        total={totalPages}
                                        current={currentPage}
                                        onClick={setCurrentPage}
                                        totalRecords={filteredTasks.length}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default withMainLayout(connect()(ProcessingTasksPage));
